/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import {
  EM_SIG_FONT_SIZE_BASE,
  SAVE_CONTACT_BUTTON_HEIGHT,
  SAVE_CONTACT_BUTTON_WIDTH,
} from '@components/EmailSignaturev2/consts';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
import { NEXT_PUBLIC_BASE_URL, NEXT_PUBLIC_IMAGE_CDN_BASE_URL } from '@env';
import { convertFontFamilyToFilenameFormat } from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
import { buildUrl } from '@modules/url-helpers';
import { useMemo } from 'react';

import styles from './EmailSignatureCompactMainSection.module.scss';

// This is the page the on vangogh for fetching/generating the save contact button image
const PATH_TO_EMAIL_SIGNATURE_SAVE_CONTACT_BUTTON_IMAGE = '/ui/email-signatures/buttons/save-contact/';

type SaveContactButtonProps = {
  emailSignature: EmailSignatureV2;
  disableAnchorLink: boolean;
  disableAllSpecialEffects: boolean;
  /** We have two ways to display the button. An actual html component (for using in the dashboard)
   * or an image generated by Vangogh of the button (for use in the email client).
   * By using an image in the email client we can ensure that the button looks the same
   * in all email clients. (eg. outlook will try to underline if we use an html anchor tag)
   */
  buttonType: 'html' | 'image';
};

/**
 * This component is used to render the save contact button in the email signature.
 * It will render either an image or an html button depending on the buttonType prop.
 *
 * @returns
 */
export const SaveContactButton: React.FC<React.PropsWithChildren<SaveContactButtonProps>> = ({
  emailSignature,
  disableAnchorLink,
  disableAllSpecialEffects,
  buttonType,
}) => {
  const buttonColor = useMemo(
    () =>
      emailSignature.saveContact.buttonColorUseThemeColor
        ? emailSignature.design.themeColor
        : emailSignature.saveContact.buttonColor,
    [
      emailSignature.design.themeColor,
      emailSignature.saveContact.buttonColor,
      emailSignature.saveContact.buttonColorUseThemeColor,
    ],
  );

  const saveContactUrl: string = useMemo(() => {
    const url = new URL(buildUrl(NEXT_PUBLIC_BASE_URL, emailSignature.saveContact.cardId));
    url.searchParams.append('utm_source', 'html_signature');

    return url.toString();
  }, [emailSignature.saveContact.cardId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const buttonComponent = useMemo(() => {
    if (buttonType === 'html') {
      return (
        <HtmlSaveContactButton
          emailSignature={emailSignature}
          disableAnchorLink={disableAnchorLink}
          buttonColor={buttonColor}
          saveContactUrl={saveContactUrl}
        />
      );
    } else {
      return (
        <ImageSaveContactButton
          emailSignature={emailSignature}
          disableAnchorLink={disableAnchorLink}
          buttonColor={buttonColor}
          saveContactUrl={saveContactUrl}
        />
      );
    }
  }, [buttonType, emailSignature, disableAnchorLink, buttonColor]);

  return (
    <td
      align="center"
      valign="top"
      onClick={(event) => {
        event.preventDefault();
        Emitter.emit('SCROLL_TO_SAVE_CONTACT');
      }}
      tabIndex={disableAllSpecialEffects ? undefined : 0}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          Emitter.emit('SCROLL_TO_SAVE_CONTACT');
        } else if (event.key === ' ') {
          event.preventDefault();
        }
      }}
      className={disableAllSpecialEffects ? undefined : styles['interactive-save-contact-button']}
    >
      <table cellSpacing="0" cellPadding="0">
        <tbody>
          <tr>{buttonComponent}</tr>
        </tbody>
      </table>
    </td>
  );
};

type HtmlSaveContactButtonProps = {
  emailSignature: EmailSignatureV2;
  disableAnchorLink: boolean;
  buttonColor: string | undefined;
  saveContactUrl: string;
};

const HtmlSaveContactButton: React.FC<React.PropsWithChildren<HtmlSaveContactButtonProps>> = ({
  emailSignature,
  disableAnchorLink,
  buttonColor,
  saveContactUrl,
}) => {
  return (
    <td
      key={
        emailSignature.saveContact.buttonColorUseThemeColor
          ? emailSignature.design.themeColor
          : emailSignature.saveContact.buttonColor
      }
      align="center"
      valign="middle"
      color={buttonColor}
      style={{
        backgroundColor: buttonColor,
        maxWidth: `${SAVE_CONTACT_BUTTON_WIDTH}px`,
        maxHeight: `${SAVE_CONTACT_BUTTON_HEIGHT}px`,
        WebkitBorderRadius: '6px',
        MozBorderRadius: '6px',
        borderRadius: '6px',
      }}
      width={SAVE_CONTACT_BUTTON_WIDTH}
      height={SAVE_CONTACT_BUTTON_HEIGHT}
    >
      <a
        href={disableAnchorLink ? undefined : saveContactUrl} // we have to set undefined here bec}
        target="_blank"
        style={{
          fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
          fontFamily: emailSignature.design.font,
          color: '#ffffff',
          fontWeight: 'normal',
          textAlign: 'center',
          backgroundColor: buttonColor,
          textDecoration: `none`,
          border: 'none',
          borderRadius: '6px',
          display: 'inline-block',
        }}
        rel="noreferrer"
      >
        <span
          style={{
            fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
            fontFamily: emailSignature.design.font,
            color: '#ffffff',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          Save contact
        </span>
      </a>
    </td>
  );
};

type ImageSaveContactButtonProps = HtmlSaveContactButtonProps;

const ImageSaveContactButton: React.FC<React.PropsWithChildren<ImageSaveContactButtonProps>> = ({
  emailSignature,
  disableAnchorLink,
  buttonColor,
  saveContactUrl,
}) => {
  const buttonImageUrl: string = useMemo(() => {
    const buttonColorWithoutHash = buttonColor?.replace('#', '');
    const buttonFont = convertFontFamilyToFilenameFormat(emailSignature.design.font);

    const buttonImageFileName = `save_${buttonColorWithoutHash}_${buttonFont}.png`;

    return buildUrl(
      NEXT_PUBLIC_IMAGE_CDN_BASE_URL,
      PATH_TO_EMAIL_SIGNATURE_SAVE_CONTACT_BUTTON_IMAGE,
      buttonImageFileName,
    );
  }, [buttonColor, emailSignature.design.font]);

  return (
    <td
      key={
        emailSignature.saveContact.buttonColorUseThemeColor
          ? emailSignature.design.themeColor
          : emailSignature.saveContact.buttonColor
      }
      align="center"
      valign="middle"
      width={SAVE_CONTACT_BUTTON_WIDTH}
      height={SAVE_CONTACT_BUTTON_HEIGHT}
    >
      <a
        href={disableAnchorLink ? undefined : saveContactUrl} // we have to set undefined here bec}
        target="_blank"
        rel="noreferrer"
      >
        <img
          src={buttonImageUrl}
          alt="Save Contact"
          width={SAVE_CONTACT_BUTTON_WIDTH}
          height={SAVE_CONTACT_BUTTON_HEIGHT}
        />
      </a>
    </td>
  );
};
